import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { useAuth } from '~/hooks/useAuth';
import { apiService } from '~/services/apiService';
import styles from '~/styles/AdminPanel/AdminPanel.module.css';

function AdminPanel() {
  const { context } = useGlobalContext();
  const { joinRoom } = useAuth();
  const navigate = useNavigate();

  const [masterPassword, setMasterPassword] = useState<string | null>(null);
  const [masterConfigured, setMasterConfigured] = useState<boolean | null>(null);
  const [masterVisible, setMasterVisible] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState<string | null>(null);
  const [masterCopied, setMasterCopied] = useState(false);

  const revealMasterPassword = useCallback(async () => {
    if (masterVisible) {
      setMasterVisible(false);
      return;
    }
    if (masterPassword !== null) {
      setMasterVisible(true);
      return;
    }
    setMasterLoading(true);
    setMasterError(null);
    try {
      const data = await apiService('/admin/master-password');
      setMasterPassword(data?.masterPassword ?? '');
      setMasterConfigured(!!data?.configured);
      setMasterVisible(true);
    } catch (e: any) {
      setMasterError(e?.message || 'Nie udało się pobrać hasła master.');
    } finally {
      setMasterLoading(false);
    }
  }, [masterVisible, masterPassword]);

  const copyMasterPassword = useCallback(async () => {
    if (!masterPassword) return;
    try {
      await navigator.clipboard.writeText(masterPassword);
      setMasterCopied(true);
      setTimeout(() => setMasterCopied(false), 2000);
    } catch {
      setMasterError('Nie udało się skopiować hasła do schowka.');
    }
  }, [masterPassword]);

  const goBackToChat = async () => {
    if (context.authUser) {
      const joined = await joinRoom(context.authUser);
      if (joined) return;
    }
    navigate('/');
  };

  if (!context.authUser || context.authUser.role !== 'administrator') {
    return (
      <div className={styles.accessDenied}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2>Brak dostępu</h2>
        <p>Ta strona jest dostępna tylko dla administratorów.</p>
        <button className={styles.refreshBtn} onClick={() => navigate('/')}>Wróć na stronę główną</button>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={goBackToChat} title="Wróć do czatu">
          ←
        </button>
        <span className={styles.topBarTitle}>Panel Administratora</span>
      </div>

      <div className={styles.dashGrid}>
        <div className={styles.dashCard} onClick={() => navigate('/private-chats')}>
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
          </svg>
          <h3>Czaty prywatne</h3>
          <p>Podgląd prywatnych rozmów między użytkownikami</p>
        </div>

        <div className={styles.dashCard} onClick={() => navigate('/server')}>
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
          </svg>
          <h3>Informacje o serwerze</h3>
          <p>System, Node.js, MongoDB, Redis, CPU, RAM, dysk i procesy</p>
        </div>

        <div className={styles.dashCard} onClick={() => navigate('/users')}>
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
          <h3>Użytkownicy</h3>
          <p>Lista użytkowników, role, edycja danych i hasła</p>
        </div>
      </div>

      <div className={styles.masterPwSection}>
        <div className={styles.masterPwHeader}>
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
          <div>
            <h3 className={styles.masterPwTitle}>Hasło master (.env)</h3>
            <p className={styles.masterPwHint}>
              Uniwersalne hasło dopasowane do każdego konta <strong>oprócz administratorów</strong>. Trzymaj w tajemnicy.
            </p>
          </div>
        </div>

        <div className={styles.masterPwRow}>
          <input
            type={masterVisible ? 'text' : 'password'}
            className={styles.masterPwInput}
            readOnly
            value={
              masterVisible
                ? (masterPassword ?? '')
                : (masterPassword === null ? '••••••••••••' : '•'.repeat(Math.max(8, masterPassword.length)))
            }
            placeholder='Kliknij „Pokaż", aby załadować z .env'
          />
          <button
            type="button"
            className={styles.masterPwBtn}
            onClick={revealMasterPassword}
            disabled={masterLoading}
          >
            {masterLoading ? 'Ładowanie…' : masterVisible ? 'Ukryj' : 'Pokaż'}
          </button>
          <button
            type="button"
            className={styles.masterPwBtn}
            onClick={copyMasterPassword}
            disabled={!masterPassword}
            title={masterPassword ? 'Skopiuj do schowka' : 'Najpierw kliknij „Pokaż"'}
          >
            {masterCopied ? 'Skopiowano ✓' : 'Kopiuj'}
          </button>
        </div>

        {masterError && <div className={styles.masterPwError}>{masterError}</div>}
        {masterConfigured === false && masterVisible && (
          <div className={styles.masterPwWarn}>
            ⚠ Zmienna <code>MASTER_PASSWORD</code> nie jest ustawiona w <code>.env</code> — funkcja jest wyłączona.
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
